<template>
  <section id="platform" class="hp-platform">
    <div class="hp-platform__container">
      <!-- 标题 -->
      <div class="hp-platform__head" data-aos="fade-up">
        <h2 class="hp-platform__title">企业级智能体平台</h2>
        <p class="hp-platform__subtitle">
          以数据驱动决策、以智能体重构业务，为品牌增长与组织效率提供端到端的AI能力
        </p>
      </div>

      <!-- Tab 切换 - 大气风格，无背景，黑色文字，蓝色下划线 -->
      <div class="hp-platform__tabs" data-aos="fade-up">
        <button
          v-for="(product, i) in products"
          :key="product.id"
          type="button"
          class="hp-platform__tab"
          @click="activeTab = i"
        >
          <div class="hp-platform__tab-inner">
            <div class="hp-platform__tab-name">{{ product.name }}</div>
            <div class="hp-platform__tab-sub">{{ product.subtitle }}</div>
          </div>
          <!-- 蓝色下划线 -->
          <div
            class="hp-platform__tab-line"
            :style="{ transform: activeTab === i ? 'scaleX(1)' : 'scaleX(0)' }"
          ></div>
        </button>
      </div>

      <!-- 产品内容卡片 -->
      <div class="hp-platform__card" data-aos="fade-up">
        <!-- 上半部分：文字介绍 + 配图 -->
        <div class="hp-platform__card-top">
          <!-- 左侧文字介绍 -->
          <div class="hp-platform__info">
            <!-- 产品名称 + 宣传语 -->
            <div class="hp-platform__info-head">
              <h3 class="hp-platform__name">{{ activeProduct.name }}</h3>
              <div
                class="hp-platform__slogan"
                :style="{
                  background: `linear-gradient(to right, ${activeProduct.color}15, transparent)`
                }"
              >
                <p :style="{ color: activeProduct.color }">{{ activeProduct.slogan }}</p>
              </div>
            </div>

            <!-- 文字介绍 -->
            <p class="hp-platform__desc">{{ activeProduct.description }}</p>

            <!-- 了解详情按钮 -->
            <router-link :to="activeProduct.link" class="hp-platform__btn">
              <span class="hp-platform__btn-text">了解详情</span>
              <LucideIcon name="arrow-right" :size="16" class="hp-platform__btn-arrow" />
            </router-link>
          </div>

          <!-- 右侧配图 - 16:9 比例，左右 10px 间距 -->
          <div class="hp-platform__media">
            <div v-if="activeProduct.image" class="hp-platform__media-box">
              <img :src="activeProduct.image" :alt="`${activeProduct.name} 产品配图`" />
            </div>
            <div v-else class="hp-platform__media-box is-placeholder">
              <div class="hp-platform__media-empty">
                <div class="hp-platform__media-empty-icon">
                  <LucideIcon :name="activeProduct.icon" :size="32" />
                </div>
                <p>产品配图</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 下半部分：4 个亮点 -->
        <div class="hp-platform__features">
          <div class="hp-platform__features-grid">
            <div v-for="(feature, i) in activeProduct.features" :key="i" class="hp-platform__feature">
              <div
                class="hp-platform__feature-icon"
                :style="{ backgroundColor: `${activeProduct.color}15` }"
              >
                <LucideIcon
                  :name="feature.icon"
                  :size="20"
                  :stroke-width="1.5"
                  :style="{ color: activeProduct.color }"
                />
              </div>
              <div class="hp-platform__feature-text">
                <h4>{{ feature.title }}</h4>
                <p>{{ feature.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// 对应 projects-vue 的 src/components/sections/PlatformSection.vue
import LucideIcon from '@/components/LucideIcon.vue'

// link 按 website-shuke 实际路由映射（见 src/config/menu.js 与 src/router/index.js），
// 原 projects-vue 里的 /products/:slug 在本项目不存在。
const PRODUCTS = [
  {
    id: 'ai-store-manager',
    name: '千橙AI超级店长',
    subtitle: 'AI零售智能体平台',
    slogan: '小门店·大模型 — 每个门店的AI超级店长',
    description:
      '对话式指令驱动，AI店长自主完成经营任务并交付结果。面向零售门店与连锁商，覆盖选品、营销、运营、销售、社群、客服等8大经营场景，搭载15+专属AI智能体，让每个门店都拥有AI超级店长。',
    color: '#F15A2E',
    icon: 'shopping-cart',
    link: '/qcSuper',
    image: require('@/assets/homePage/chaojidianzhang-product.jpg'),
    features: [
      {
        icon: 'shopping-cart',
        title: '全渠道智能卖货',
        desc: '线上商城、社群、O2O多渠道一键卖货；TOP品牌供应链直供，AI辅助选品上架与营销素材生成'
      },
      {
        icon: 'bot',
        title: 'AI店长智能经营',
        desc: '对话式指令，AI店长7×24小时自主完成经营，百问百答、营销策划，降低人工依赖，提升人效坪效'
      },
      {
        icon: 'bar-chart-3',
        title: '数据驱动精细管理',
        desc: '进销存数据一屏统览；AI智能盘点补货、销售预测与异常预警，从"凭经验"到"看数据"科学决策'
      },
      {
        icon: 'users',
        title: '会员全生命周期营销',
        desc: 'AI会员画像与智能分层，自动化场景营销；新客引流、老客复购、私域社群变现，挖掘老客价值'
      }
    ]
  },
  {
    id: 'leapo-ai',
    name: 'LeapoAI',
    subtitle: '企业级AI智能体平台',
    slogan: '让1个人的经验复制到全公司',
    description:
      '为零售而生的企业级AI平台，五大AI数字员工覆盖店长、导购、客服、运营、采购全岗位。不只是通用AI，而是"懂零售"的AI，支持产品订阅、一体机私有化、FDE驻场三种交付模式，快速对接企业数字系统。',
    color: '#025EDA',
    icon: 'bot',
    link: '/leapoAI',
    image: require('@/assets/homePage/leapoai-product.jpg'),
    features: [
      {
        icon: 'cpu',
        title: 'AI数字员工',
        desc: '覆盖销售市场、财务行政、人力法务、供应链运营全职能，7×24小时在线协同，让1个人的经验复制到全公司'
      },
      {
        icon: 'workflow',
        title: '企业系统对接',
        desc: '快速对接ERP、CRM、OA、WMS等内部系统，打破数据孤岛，AI数字员工实时调取业务数据进行分析决策'
      },
      {
        icon: 'shield',
        title: '企业级安全管控',
        desc: 'Token计量+RBAC权限+全链路审计，细粒度权限控制与调用审计，满足金融/政务级合规要求'
      },
      {
        icon: 'server',
        title: '灵活部署模式',
        desc: '支持产品订阅、一体机私有化、FDE驻场三种交付模式，AI专家深入现场，不只交付产品更交付业务结果'
      }
    ]
  },
  {
    id: 'jiuci-token',
    name: '九词 NineToken',
    subtitle: 'AI内容创作平台',
    slogan: '每个想法 都能被看见',
    description:
      '覆盖主流大模型，剧本 · 分镜、AI 生图、AI 生视频、AI 配音一次搞定；一个积分账户全场景通用，创作不再东拼西凑。',
    color: '#A0845C',
    icon: 'zap',
    link: '/nineToken',
    image: require('@/assets/homePage/jiuci-product.jpg'),
    features: [
      {
        icon: 'layers',
        title: '一站式创作',
        desc: '剧本、生图、生视频、配音，不再切来切去、到处倒数据；从选题到出稿一处做完，效率翻倍。'
      },
      {
        icon: 'zap',
        title: '零门槛 · 即开即用',
        desc: '注册即可上手，无需复杂配置；提个需求就能出作品，不用学提示词、不啃教程，小白也能用。'
      },
      {
        icon: 'shield-check',
        title: '稳定 · 安全 · 放心用',
        desc: '长期稳定运行，高峰时段也流畅不卡；内容经过安全审核，合规有保障，商业创作踏实放心。'
      },
      {
        icon: 'sparkles',
        title: '智能调度 · 效果更佳',
        desc: '聚合主流大模型，按你的创作内容自动选对模型，效果始终在线；不用钻研参数、反复试错，省心省力，出片更快。'
      }
    ]
  }
]

export default {
  name: 'PlatformSection',
  components: { LucideIcon },
  data() {
    return {
      products: PRODUCTS,
      activeTab: 0
    }
  },
  computed: {
    activeProduct() {
      return this.products[this.activeTab]
    }
  }
}
</script>

<style lang="less" scoped>
@import './vars.less';

.hp-platform {
  position: relative;
  .hp-section-padding();
  background: #f8f9fa;
}

.hp-platform__container {
  .hp-container();
}

// ---------- 标题 ----------
.hp-platform__head {
  text-align: center;
  margin-bottom: 48px;
}
.hp-platform__title {
  .hp-section-title();
}
.hp-platform__subtitle {
  .hp-section-subtitle();
  max-width: 768px;
}

// ---------- Tab ----------
.hp-platform__tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 32px;
  gap: 24px;

  @media (min-width: @screen-md) {
    gap: 64px;
  }
  @media (min-width: @screen-lg) {
    gap: 96px;
  }
}
.hp-platform__tab {
  position: relative;
  padding: 0 0 12px;
  border: 0;
  background: transparent;
  cursor: pointer;
  flex: 1 1 0%;
  min-width: 0;

  @media (min-width: @screen-md) {
    flex: none;
  }
}
.hp-platform__tab-inner {
  text-align: center;
}
.hp-platform__tab-name {
  .hp-truncate();
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  color: @hp-ink;

  @media (min-width: @screen-md) {
    font-size: 18px;
  }
  @media (min-width: @screen-lg) {
    font-size: 20px;
  }
}
.hp-platform__tab-sub {
  .hp-truncate();
  font-size: 12px;
  color: @hp-gray;

  @media (min-width: @screen-md) {
    font-size: 14px;
  }
}
.hp-platform__tab-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 9999px;
  background: @hp-blue;
  transition: transform 0.5s cubic-bezier(0, 0, 0.2, 1);
}

// ---------- 产品卡片 ----------
.hp-platform__card {
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  border: 1px solid @hp-border;
  text-align: left;
}
.hp-platform__card-top {
  display: flex;
  flex-direction: column;

  @media (min-width: @screen-lg) {
    flex-direction: row;
    align-items: center;
  }
}

// 左侧文字
.hp-platform__info {
  flex: 1 1 0%;
  padding: 32px;

  @media (min-width: @screen-md) {
    padding: 40px;
  }
  @media (min-width: @screen-lg) {
    padding: 48px;
  }
}
.hp-platform__info-head {
  margin-bottom: 24px;
}
.hp-platform__name {
  margin: 0 0 12px;
  font-size: 30px;
  font-weight: 700;
  color: @hp-ink;

  @media (min-width: @screen-md) {
    font-size: 32px;
  }
}
.hp-platform__slogan {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 8px;

  p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;

    @media (min-width: @screen-md) {
      font-size: 16px;
    }
  }
}
.hp-platform__desc {
  margin: 0 0 32px;
  font-size: 15px;
  line-height: 1.625;
  color: @hp-gray;
}

// 了解详情按钮
.hp-platform__btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  padding: 12px 40px;
  border-radius: 10px;
  background: @hp-ink;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: box-shadow 0.3s ease;

  &:hover {
    color: #fff;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);

    .hp-platform__btn-text {
      transform: translateX(-8px);
    }
    .hp-platform__btn-arrow {
      opacity: 1;
      transform: translateX(0);
    }
  }
}
.hp-platform__btn-text {
  transition: transform 0.3s ease;
}
.hp-platform__btn-arrow {
  position: absolute;
  right: 16px;
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

// 右侧配图
.hp-platform__media {
  width: 100%;
  flex-shrink: 0;
  padding: 0 10px;

  @media (min-width: @screen-lg) {
    width: 600px;
  }
}
.hp-platform__media-box {
  width: 100%;
  overflow: hidden;
  .hp-aspect(16; 9);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &.is-placeholder {
    background: #f5f5f7;

    > * {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
.hp-platform__media-empty {
  text-align: center;
  color: @hp-gray-light;
  flex-direction: column;

  p {
    margin: 0;
    font-size: 14px;
  }
}
.hp-platform__media-empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
  border-radius: 9999px;
  background: #e8e8ed;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cccccc;
}

// ---------- 4 个亮点 ----------
.hp-platform__features {
  border-top: 1px solid @hp-border;
  padding: 24px 32px;

  @media (min-width: @screen-md) {
    padding: 32px 40px;
  }
  @media (min-width: @screen-lg) {
    padding: 32px 48px;
  }
}
.hp-platform__features-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: @screen-md) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px;
  }
}
.hp-platform__feature {
  display: flex;
  align-items: flex-start;
}
.hp-platform__feature-icon {
  width: 40px;
  height: 40px;
  margin-right: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hp-platform__feature-text {
  flex: 1 1 0%;
  min-width: 0;

  h4 {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 600;
    color: @hp-ink;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.625;
    color: @hp-gray;
  }
}
</style>
